import BasicInformation from './BasicInformation';
import ConfirmDetails from './ConfirmDetails';
import InstitutionInformation from './InstitutionInformation';
import FacilitySearch from './FacilitySearch';
import React, { useContext, useEffect, useState } from 'react';
import { BasicInfo, Facility, Institution, RegistrationValue } from '../interfaces/Interfaces';
import RegistrationStepCount, {StepName} from './RegistrationStepCount';
import { getNameDisplay, isAppDataValid } from './Applications';
import { getAppComponent } from './Applications';
import { userDataCtx } from '../hooks/applicationData';


interface FormTrackerProps {
    formStep: number,
    setFormStep: React.Dispatch<React.SetStateAction<number>>,
    userType: "customer" | "independent" | "student" | null,
    lang: string | null,
    userCountry: string | null,
    setUserCountry: React.Dispatch<React.SetStateAction<string | null>>,
    objectId: string | null,
    appId: string | null,
    translate: () => void,
    setBackgroundWIP: React.Dispatch<React.SetStateAction<boolean>>,
    email: string,
    basicInfo: BasicInfo,
    setBasicInfo: React.Dispatch<React.SetStateAction<BasicInfo>>,
    bdBioSciencesUserType: string | null,
    setBdBioSciencesUserType: React.Dispatch<React.SetStateAction<string | null>>,
    facZip: string,
    setFacZip: React.Dispatch<React.SetStateAction<string>>,
    positions: Array<[string, string]>,
    facilityInfo: Facility,
    setFacilityInfo: (arg0: Facility) => void,
    institute: Institution,
    setInstitute: React.Dispatch<React.SetStateAction<Institution>>,
    getJWTDriver: () => Promise<boolean>,
    CONFIG: any,
    countries?: Record<string, string>,
    token: string,
    setTitleGuid: React.Dispatch<React.SetStateAction<string>>,
    facilityOptional: boolean,
    skipOptionalFacility: boolean,
    institutionLinkAlwaysOptional: boolean,
    skipOptionalInstitutionLink: boolean,
    appFeatures: any;
    authLocale: string,
    accountID: string,
    hideFields: Array<string>,
    optionalFields: Array<string>,
    customRegistrationValues: Array<RegistrationValue>,
    updateRegistrationValues: () => void,
    skipBDLAStep: boolean
}

const FormTracker = (props: FormTrackerProps) => {
    const [userDefinedInstitute, setUserDefinedInstitute] = useState(false);


    useEffect(() => {
        props.setTitleGuid('5c065f06-c0cd-4ddd-8413-ef190b6d09b5');
    }, []);


    // Effect to resize container, also update any custom fields that might now be available
    useEffect(() => {
        try {
            props.updateRegistrationValues();
            $('.match-height').matchHeight();
        } catch (err) {
            console.error("JQuery is not available");
        }
    }, [props.formStep])

    const {data, setValidationFlags} = useContext(userDataCtx);
    const goBack = () => {props.setFormStep(props.formStep - 1)};
    const goForward = () => {
        // Currently only used for Applciation specific data so putting validation logic here without any sort of conditional (only will make sense on the app data specific screen)
        if(isAppDataValid(props.appId, data, setValidationFlags))
            props.setFormStep(props.formStep + 1);
    };
    const [appNameDisplay, appNameDisplayNeeded] = getNameDisplay(props);
    const [appInfoComp, _] = getAppComponent(props, goBack, goForward);
    const steps: StepName[] = ["basicInfo"];
    
    if (props.userType === 'student') {
        if (props.institutionLinkAlwaysOptional && props.skipOptionalInstitutionLink) {
            console.log("For student user: both inst fields are true so no display of institution");
        }
        else {
            steps.push("instituteInfo");
        }
    }
    // "Customer will have current experience"
    else if (props.userType !== "independent" && !props.facilityOptional) steps.push("facilityInfo");
    else if (!props.skipOptionalFacility && !props.optionalFields?.includes("facilityZip")) steps.push("facilityInfo");
    
    //console.log("value for variables appNameDisplayNeeded:",appNameDisplayNeeded, " appNameDisplay: ",appNameDisplay)
    if (appNameDisplayNeeded) steps.push("customApp")     
    steps.push("confirm");

    //form tracker starts on step 2
    // All manipulations of the steps array should happen before this line
    const stepName = steps[props.formStep - 2];

    var formHeader = (step: StepName) => {
        switch (step) {
            case "basicInfo":
                return (<h2 className="step-title a5c3e18d-52d8-49a0-bb87-6eff2eb60686"> Basic Information </h2>)
            case "facilityInfo":
                return (<h2 className="step-title c63f87ff-83bb-4386-b6dd-2ee5ae2d51ca"> Facility Information </h2>)
            case "instituteInfo":
                return (<h2 className="step-title fd55a358-0575-4df0-89b6-956ee7078ff6"> Institution Information </h2>)
            case "customApp":
                return appNameDisplay;
            case "confirm":
                return (<h2 className="step-title 3840eb46-2ee4-46e3-906e-66878c30d380"> Confirm Details </h2>)
            default:
                return (<h2>Currently Unavailable</h2>)
        }
    }
    var formContent = (step: StepName) => {
        switch (step) {
            case "basicInfo":
                return (
                    <BasicInformation
                        email={props.email}
                        formStep={props.formStep}
                        setFormStep={props.setFormStep}
                        basicInfo={props.basicInfo}
                        setBasicInfo={props.setBasicInfo}
                        userType={props.userType}
                        userCountry={props.userCountry}
                        objectId={props.objectId}
                        appId={props.appId}
                        translate={props.translate}
                        facZip={props.facZip}
                        setFacZip={props.setFacZip}
                        setBackgroundWIP={props.setBackgroundWIP}
                        bdBioSciencesUserType={props.bdBioSciencesUserType}
                        setBdBioSciencesUserType={props.setBdBioSciencesUserType}
                        positions={props.positions}
                        lang={props.lang}
                        authLocale={props.authLocale}
                        customRegistrationValues={props.customRegistrationValues}
                        updateRegistrationValues={props.updateRegistrationValues}
                        appFeatures={props.appFeatures}
                        hideFields= {props.hideFields}
                        optionalFields= {props.optionalFields}
                        skipOptionalFacility= {props.skipOptionalFacility}
                        accountID= {props.accountID}
                        token={props.token}
                        facilityOptional={props.facilityOptional}
                        facilityInfo={props.facilityInfo}
                        setFacilityInfo={props.setFacilityInfo}
                    />
                )
            case "facilityInfo":                
                return (
                        <FacilitySearch
                            formStep={props.formStep}
                            setFormStep={props.setFormStep}
                            facilityInfo={props.facilityInfo}
                            setFacilityInfo={props.setFacilityInfo}
                            facZip={props.facZip}
                            facilityOptional={props.facilityOptional}
                            appFeatures={props.appFeatures}
                            userCountry={props.userCountry}
                            accountNum={props.basicInfo.accountNumber}
                            userType={props.userType}
                            translate={props.translate}
                            setBackgroundWIP={props.setBackgroundWIP}
                            countries={props.countries}
                            lang={props.lang}
                            token={props.token}                            
                        /> );
            case "instituteInfo":
                    return <InstitutionInformation
                        formStep={props.formStep}
                        setFormStep={props.setFormStep}
                        institute={props.institute}
                        setInstitute={props.setInstitute}
                        translate={props.translate}
                        setBackgroundWIP={props.setBackgroundWIP}
                        other={userDefinedInstitute}
                        setOther={setUserDefinedInstitute}
                        countries={props.countries}
                        lang={props.lang}
                        token={props.token}
                        institutionLinkAlwaysOptional={props.institutionLinkAlwaysOptional}
                        skipOptionalInstitutionLink={props.skipOptionalInstitutionLink}
                        appFeatures={props.appFeatures}
                    />
            case "customApp":
                return appInfoComp;
            case "confirm":
                return (
                    <ConfirmDetails
                        email={props.email}
                        basicInfo={props.basicInfo}
                        facilityInfo={props.facilityInfo}
                        facZip={props.facZip}
                        institute={props.institute}
                        formStep={props.formStep}
                        setFormStep={props.setFormStep}
                        userType={props.userType}
                        lang={props.lang}
                        userCountry={props.userCountry}
                        objectId={props.objectId}
                        appId={props.appId}
                        translate={props.translate}
                        setBackgroundWIP={props.setBackgroundWIP}
                        bdBioSciencesUserType={props.bdBioSciencesUserType}
                        getJWTDriver={props.getJWTDriver}
                        CONFIG={props.CONFIG}
                        token={props.token}
                        positions={props.positions}
                        countries={props.countries}
                        institutionLinkAlwaysOptional={props.institutionLinkAlwaysOptional}
                        skipOptionalInstitutionLink={props.skipOptionalInstitutionLink}
                        authLocale={props.authLocale}
                        customRegistrationValues={props.customRegistrationValues}
                        updateRegistrationValues={props.updateRegistrationValues}
                        appFeatures={props.appFeatures}
                        hideFields= {props.hideFields}
                        optionalFields= {props.optionalFields}
                    />
                )
            default:
                return (
                    <div>
                        <p className="51bdc345-3dc9-47f8-b70e-198aab4c1bcd"> Currently unavailable </p>
                    </div>
                )
        }
    }

    return (
        <>
            <div className='row'>
                <RegistrationStepCount 
                    steps={steps} 
                    step={props.formStep}
                    appId={props.appId}
                    userType={props.userType}
                    CONFIG={props.CONFIG} />
                <div className="col-sm-8 col-md-9">
                    <div className="register-main match-height">
                        <div className="step">
                            <div className="header">
                                <div className="step-place">
                                    <span className="step-text 05017e1d-45fd-42db-ab4a-740649a6635c">Step</span>
                                    <span className="step-number">{props.formStep}</span>
                                </div>
                                <>{formHeader(stepName)}</>
                            </div>
                        </div>
                        <div className="step-content">
                            {(props.institutionLinkAlwaysOptional === true && stepName == "instituteInfo" ) ?
                                <p className="b8df39ce-978b-44fc-a1e6-e1f4eb3611f7">Optional: You may link your institution to your account below.</p>
                                :
                                <p className={stepName === "confirm" ? "hide 346dc068-00af-43ac-9f72-d6f252858b88" : "346dc068-00af-43ac-9f72-d6f252858b88"}>All fields marked as * are required.</p>
                            }
                            <>{formContent(stepName)}</>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default FormTracker
